import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { distinctUntilKeyChanged } from 'rxjs/operators';
import { get } from 'lodash';
import { AppState } from 'app/app.reducer';
import { Content } from 'app/constants';
import { Paginator } from 'primeng/paginator';
import * as actions from 'redux/actions';
import {
  AuthenticationService,
  NewsFeedService,
  SearchFilterDefaultsService,
} from 'app/services';
import { OverlayPanel } from 'primeng/overlaypanel';
import { fromEvent, Observable } from 'rxjs';
import { Notification, Search } from 'app/models';
import { ExportFilenameService } from 'app/components';

@Component({
  selector: 'app-news-feed',
  templateUrl: './news-feed.component.html',
  styleUrls: ['./news-feed.component.scss'],
})
export class NewsFeedComponent implements OnInit {
  public parentName: string;
  public feeds: any[] = [];
  public loading: boolean;
  public pageSize: number;
  public page: number;
  public total: number;
  public totalPages: number;
  public currentPage: number;
  public projectId: number;
  public projectName: string;
  public sortSelected: string;
  public hideSort: boolean;
  public isLoadingContent: boolean;
  public isPanelShowed: boolean;
  public body$: Observable<any>;
  public authorsQuery: string;
  public outletsQuery: string;
  public topicsQuery: string;
  public contentItemsQuery: string;
  public onResetFilter: boolean;
  public showFilters: boolean;
  public newsFeedFilters: Search;
  public isSidebarVisible: boolean;
  public isExportingExcel: boolean;
  public isExportingText: boolean;
  private ignoreDefaultsInitialized: boolean;

  @ViewChild('pp', { static: false }) paginator: Paginator;
  @ViewChild('opShowQuery', { static: false }) op: OverlayPanel;
  @ViewChild('btnShowQuery', { static: false }) btnShowQuery!: ElementRef;

  constructor(
    private store: Store<AppState>,
    private newsFeedService: NewsFeedService,
    private auth: AuthenticationService,
    private exportFilenameService: ExportFilenameService,
    private searchFilterDefaults: SearchFilterDefaultsService
  ) {
    this.parentName = Content.NewsFeed;
    this.feeds = [];
    this.loading = false;
    this.pageSize = 50;
    this.page = 1;
    this.projectId = null;
    this.currentPage = 0;
    this.sortSelected = 'recent';
    this.hideSort = false;
    this.isPanelShowed = false;
    this.body$ = fromEvent(document, 'click');
    this.authorsQuery = '';
    this.outletsQuery = '';
    this.topicsQuery = '';
    this.contentItemsQuery = '';
    this.onResetFilter = true;
    this.showFilters = false;
    this.newsFeedFilters = this.searchFilterDefaults.createSearch();
    this.ignoreDefaultsInitialized = this.searchFilterDefaults.getIgnoreLabelIds().length > 0;
    this.isExportingExcel = false;
    this.isExportingText = false;
  }

  ngOnInit() {
    this.store.select('category').subscribe(() => {
      const hasIgnoreDefaults = this.searchFilterDefaults.getIgnoreLabelIds().length > 0;
      if (!this.ignoreDefaultsInitialized && hasIgnoreDefaults) {
        this.ignoreDefaultsInitialized = true;
        if (this.projectId) {
          this.getContentItems();
        }
      }
    });
    this.body$.subscribe(() => {
      if (this.isPanelShowed) {
        this.hideOverlayPanel();
      }
    });
    this.store.select('ui').subscribe(({ visible }) => {
      this.isSidebarVisible = visible;
    });
    this.store.select('project').subscribe(({ projectName }) => {
      this.projectName = projectName;
    });
    this.store
      .select('project')
      .pipe(distinctUntilKeyChanged('projectSelected'))
      .subscribe(({ projectSelected }) => {
        if (projectSelected) {
          this.projectId = projectSelected?.value;
          this.getContentItems();
        }
      });

    this.store
      .select('newsFeed')
      .subscribe(({ projectId: number, data, loaded, loading }) => {
        this.isLoadingContent = loading;
        this.loading = loaded;
        this.feeds = [];
        const contentItems = get(data, 'content', []);
        if (contentItems.length > 0) {
          this.feeds = contentItems;
          this.currentPage = data.number;
          this.page = data.number + 1;
          this.totalPages = data.totalPages;
          this.total = data.totalElements;
        } else {
          this.feeds = [];
          this.currentPage = 0;
          this.page = 0;
          this.totalPages = 0;
          this.total = 0;
        }
        if (loaded) {
          setTimeout(() => {
            this.store.dispatch(actions.stopLoading());
          }, 1000);
        }
        // this.scrollTop();
      });
  }

  public toggleModal() {
    this.showFilters = !this.showFilters;
  }

  public handleVisible(visible) {
    this.showFilters = visible;
  }

  public handleNewsFeedwithFilters(search: Search) {
    this.newsFeedFilters = search;
    this.ignoreDefaultsInitialized = true;
    this.getContentItems();
  }

  public handleShowQuery(event?: Event) {
    if (this.isPanelShowed) {
      this.hideOverlayPanel();
    } else {
      this.newsFeedService
        .myNewsFeedQuery(this.projectId)
        .subscribe((response) => {
          const { authorsQuery, outletsQuery, topicsQuery, contentItemsQuery } = response;
          this.authorsQuery = authorsQuery || '';
          this.outletsQuery = outletsQuery || '';
          this.topicsQuery = topicsQuery || '';
          this.contentItemsQuery = contentItemsQuery || '';
          this.showOverlayPanel(event);
        });
    }
  }

  handleExportToExcel() {
    this.exportFilenameService
      .prompt(this.getDefaultExportFilename('xls'))
      .subscribe((filename) => {
        if (filename) {
          this.exportToExcel(filename);
        }
      });
  }

  private getDefaultExportFilename(extension: 'xls' | 'txt'): string {
    const date = new Date();
    const formattedDate = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
    return `TechNews_${this.projectName + '_' + formattedDate}.${extension}`;
  }

  private exportToExcel(filename: string) {
    this.isExportingExcel = true;
    this.newsFeedService
      .myNewsFeedExportToExcel(this.projectId, this.newsFeedFilters)
      .subscribe({
        next: (response: Blob) => {
          if (response && response.size > 0) {
            const blob = new Blob([response], {
              type: 'application/vnd.ms-excel',
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
              this.isExportingExcel = false;
            }, 100);
          } else {
            this.isExportingExcel = false;
            this.store.dispatch(
              actions.showNotification({
                notify: new Notification(
                  'No data received from server',
                  'error',
                  'tr'
                ),
              })
            );
          }
        },
        error: (error) => {
          this.isExportingExcel = false;
          this.store.dispatch(
            actions.showNotification({
              notify: new Notification(
                'Error exporting to Excel',
                'error',
                'tr'
              ),
            })
          );
        },
      });
  }

  handleExportToText() {
    this.exportFilenameService
      .prompt(this.getDefaultExportFilename('txt'))
      .subscribe((filename) => {
        if (filename) {
          this.exportToText(filename);
        }
      });
  }

  private exportToText(filename: string) {
    this.isExportingText = true;
    this.newsFeedService
      .myNewsFeedExportToText(this.projectId, this.newsFeedFilters)
      .subscribe({
        next: (response: string) => {
          const blob = new Blob([response], {
            type: 'text/plain;charset=utf-8',
          });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();

          setTimeout(() => {
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            this.isExportingText = false;
          }, 100);
        },
        error: (error) => {
          this.isExportingText = false;
          this.store.dispatch(
            actions.showNotification({
              notify: new Notification(
                'Error exporting to text',
                'error',
                'tr'
              ),
            })
          );
        },
      });
  }

  handleCopyQuery(query) {
    navigator.clipboard.writeText(query).then(() => {
      console.log('Query copied to clipboard');
      this.store.dispatch(
        actions.showNotification({
          notify: new Notification(
            'Query copied to clipboard',
            'success',
            'tr'
          ),
        })
      );
    });
  }

  public showOverlayPanel(event?: Event) {
    this.op.show(event, this.btnShowQuery.nativeElement);
    this.isPanelShowed = true;
  }

  public hideOverlayPanel() {
    this.op.hide();
    this.isPanelShowed = false;
  }

  private getContentItems() {
    this.store.dispatch(actions.startLoading());
    this.store.dispatch(
      actions.setNewsFeed({
        projectId: this.projectId,
        sort: this.sortSelected,
        filters: this.newsFeedFilters,
        page: this.currentPage,
        size: this.pageSize,
      })
    );
  }

  public sortChange(e) {
    this.sortSelected = e;
    this.currentPage = 0;
    this.page = 1;
    this.resetPaginator();
    this.getContentItems();
  }

  getPage({ page }) {
    this.currentPage = page;
    this.page = page + 1;
    this.getContentItems();
  }

  resetPaginator() {
    if (this.paginator) {
      this.paginator.changePage(0);
    }
  }

  isAdmin() {
    return this.auth.isAdminUser();
  }
}
